import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetchMessagesStats } from '@pages/event/api/useFetchMessagesStats';
import { SendContentType } from '@pages/event/api/useSendMessages';
import { MAX_NUMBER_ATTEMPTS, SEND_FLOW_PHASE, SEND_MESSAGES_POLL_INTERVAL_MS, type SendFlowPhase } from './send-flow';

interface UseSendMessagesFlowOptions {
  eventId: number;
  contentType?: SendContentType;
  onComplete?: () => void;
}

export const useSendMessagesFlow = ({
  eventId,
  contentType = SendContentType.Invite,
  onComplete,
}: UseSendMessagesFlowOptions) => {
  const [phase, setPhase] = useState<SendFlowPhase>(SEND_FLOW_PHASE.IDLE);
  const [messageAttempt, setMessageAttempt] = useState<number | null>(null);
  const attempt = useRef(0);

  const isTrackingStats = phase !== SEND_FLOW_PHASE.IDLE && messageAttempt !== null;

  const {
    messagesStats,
    error: messagesStatsError,
    refetch: refetchStats,
  } = useFetchMessagesStats(
    {
      eventId,
      attempt: messageAttempt,
      contentType,
    },
    !isTrackingStats
  );

  const refetchStatsRef = useRef(refetchStats);
  refetchStatsRef.current = refetchStats;

  useEffect(() => {
    if (phase !== SEND_FLOW_PHASE.LOADING || messageAttempt === null) {
      return;
    }

    if (messagesStats?.isSent || messagesStatsError) {
      setPhase(SEND_FLOW_PHASE.STATISTICS);
    }

    if (phase === SEND_FLOW_PHASE.LOADING && attempt.current > MAX_NUMBER_ATTEMPTS) {
      setPhase(SEND_FLOW_PHASE.STARTED);
      return;
    }
  }, [phase, messageAttempt, messagesStats, messagesStatsError]);

  useEffect(() => {
    if (phase !== SEND_FLOW_PHASE.LOADING || messageAttempt === null || messagesStats?.isSent) {
      return;
    }

    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    const pollStats = () => {
      if (cancelled) {
        return;
      }

      attempt.current = attempt.current + 1;

      refetchStatsRef.current();
      timeoutId = setTimeout(pollStats, SEND_MESSAGES_POLL_INTERVAL_MS);
    };

    attempt.current = 1;
    timeoutId = setTimeout(pollStats, SEND_MESSAGES_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [phase, messageAttempt, messagesStats?.isSent]);

  const closeStatistics = useCallback(() => {
    setPhase(SEND_FLOW_PHASE.IDLE);
    setMessageAttempt(null);
    onComplete?.();
  }, [onComplete]);

  const runSend = useCallback(async (send: () => Promise<number | undefined>) => {
    try {
      setPhase(SEND_FLOW_PHASE.LOADING);
      const attempt = await send();

      if (attempt === undefined) {
        setPhase(SEND_FLOW_PHASE.IDLE);
        return;
      }

      setMessageAttempt(attempt);
    } catch {
      setPhase(SEND_FLOW_PHASE.IDLE);
      setMessageAttempt(null);
    }
  }, []);

  return {
    runSend,
    closeStatistics,
    isWaitingForSend: phase === SEND_FLOW_PHASE.LOADING,
    isStatisticsModalVisible: phase === SEND_FLOW_PHASE.STATISTICS,
    isSendFlowActive: phase !== SEND_FLOW_PHASE.IDLE,
    isStartedFlowActive: phase === SEND_FLOW_PHASE.STARTED,
    messagesStats,
    messageAttempt,
  };
};
